// api/notifications.ts
/**
 * API client for notification endpoints.
 */

import { apiJson } from "./client";

// ============================================================================
// Types
// ============================================================================

export type NotificationType = "success" | "error" | "info" | "warning";

export type AppNotification = {
  public_id: string;
  type: NotificationType;
  title: string;
  message?: string | null;
  category?: string | null;
  action_url?: string | null;
  show_toast: boolean;
  toast_duration?: number | null;
  bar_duration?: number | null;
  created_at: string;
  expires_at?: string | null;
  auto_read_at?: string | null;
  is_read: boolean;
  read_at?: string | null;
  is_dismissed: boolean;
};

export type NotificationListResponse = {
  items: AppNotification[];
  total: number;
  has_more: boolean;
};

export type UnreadCountResponse = {
  count: number;
  /**
   * Map of data_source public_id → last_cache_refill_at ISO timestamp.
   * Piggybacks on the 30s notifications poll to drive client-side
   * dashboard-cache invalidation. See plan
   * `docs/plans-pending/2026-05-19-server-side-dashboard-cache-refill.md`
   * Step 11. Empty when the account has no managed pipelines.
   */
  cache_watermarks?: Record<string, string>;
};

export type CreateNotificationPayload = {
  title: string;
  message?: string;
  type?: NotificationType;
  category?: string;
  action_url?: string;
  show_toast?: boolean;
  toast_duration?: number;
  bar_duration?: number;
  auto_read_after_ms?: number;
};

export type PushNotificationPayload = {
  account_public_id: string;
  title: string;
  message?: string;
  type?: NotificationType;
  category?: string;
  action_url?: string;
  show_toast?: boolean;
  toast_duration?: number;
  bar_duration?: number;
  expires_at?: string;
};

// ============================================================================
// User endpoints
// ============================================================================

export type NotificationFilters = {
  type?: NotificationType;
  category?: string;
  is_read?: boolean;
};

export async function getNotifications(
  limit = 50,
  offset = 0,
  filters?: NotificationFilters
): Promise<NotificationListResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (filters?.type) params.set("type", filters.type);
  if (filters?.category) params.set("category", filters.category);
  if (filters?.is_read !== undefined) params.set("is_read", String(filters.is_read));
  return apiJson<NotificationListResponse>(`/notifications/?${params.toString()}`);
}

export async function getUnreadCount(): Promise<UnreadCountResponse> {
  return apiJson<UnreadCountResponse>("/notifications/unread-count");
}

export async function createNotification(
  data: CreateNotificationPayload
): Promise<AppNotification> {
  return apiJson<AppNotification>("/notifications/", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function markAsRead(publicId: string): Promise<void> {
  await apiJson(`/notifications/${publicId}/read`, { method: "POST" });
}

export async function markAllAsRead(): Promise<void> {
  await apiJson("/notifications/read-all", { method: "POST" });
}

export async function dismissNotification(publicId: string): Promise<void> {
  await apiJson(`/notifications/${publicId}/dismiss`, { method: "POST" });
}

export async function dismissAllNotifications(): Promise<void> {
  await apiJson("/notifications/dismiss-all", { method: "POST" });
}

// ============================================================================
// Superadmin endpoints
// ============================================================================

export async function pushNotification(
  data: PushNotificationPayload
): Promise<{ ok: boolean; public_id: string }> {
  return apiJson("/superadmin/notifications/", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getAccountNotifications(
  accountPublicId: string,
  limit = 100,
  offset = 0
): Promise<NotificationListResponse> {
  return apiJson<NotificationListResponse>(
    `/superadmin/notifications/accounts/${accountPublicId}?limit=${limit}&offset=${offset}`
  );
}

export async function deleteNotification(publicId: string): Promise<void> {
  await apiJson(`/superadmin/notifications/${publicId}`, {
    method: "DELETE",
  });
}
